import { useState, type MouseEvent } from "react";
import { Icon } from "../Icon";

export interface MenuState {
  id: string;
  left: number;
  bottom: number;
}

export interface PagesBarProps {
  ids: string[];
  index: number;
  thumbs: Record<string, string | undefined>;
  ratio: number;
  onGo: (index: number) => void;
  onAdd: () => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
}

/** Page thumbnails under the design: switch, add, duplicate, delete and reorder pages. */
export function PagesBar({
  ids,
  index,
  thumbs,
  ratio,
  onGo,
  onAdd,
  onDuplicate,
  onDelete,
  onMove,
}: PagesBarProps) {
  const [menu, setMenu] = useState<MenuState | null>(null);

  const h = 54;
  const w = Math.max(30, Math.min(110, Math.round(h * ratio)));

  return (
    <div
      className="
        flex
        min-w-0
        flex-1
        items-center
        gap-3
      "
      aria-label="Pages"
    >
      {/* Page thumbnails */}
      <div
        className="
          flex
          min-w-0
          items-center
          gap-2.5
          overflow-x-auto
          px-0.5
          py-1
          no-scrollbar
        "
      >
        {ids.map((id, i) => {
          const isActive = i === index;
          const isMenuOpen = menu?.id === id;

          return (
            <div
              key={id}
              className="relative flex-none"
            >
              {/* Thumbnail */}
              <button
                type="button"
                className={`
                  relative
                  block
                  cursor-pointer
                  overflow-hidden
                  rounded-[6px]
                  border-0
                  bg-st-paper
                  p-0
                  transition
                  focus-visible:outline
                  focus-visible:outline-[3px]
                  focus-visible:outline-offset-2
                  focus-visible:outline-[rgba(11,122,90,0.45)]
                  ${
                    isActive
                      ? "shadow-[0_0_0_2px_#0b7a5a]"
                      : "shadow-[0_0_0_1px_#dbe3df]"
                  }
                `}
                style={{
                  width: w,
                  height: h,
                }}
                onClick={() => onGo(i)}
                aria-label={`Page ${i + 1}`}
                aria-current={isActive ? "page" : undefined}
              >
                {thumbs[id] ? (
                  <img
                    src={thumbs[id]}
                    alt=""
                    className="
                      block
                      h-full
                      w-full
                      rounded-none
                      object-cover
                    "
                  />
                ) : (
                  <span
                    className="
                      block
                      h-full
                      w-full
                      animate-pulse
                      rounded-none
                      bg-st-paper
                    "
                  />
                )}

                {/* Page number */}
                <span
                  className="
                    absolute
                    bottom-[3px]
                    left-[3px]
                    min-w-4
                    rounded-[4px]
                    bg-[rgba(14,28,24,0.75)]
                    px-1
                    py-px
                    text-center
                    text-[10px]
                    font-bold
                    leading-normal
                    text-white
                  "
                >
                  {i + 1}
                </span>
              </button>

              {/* More button */}
              <button
                type="button"
                className={`
                  absolute
                  right-0.5
                  top-0.5
                  grid
                  h-[22px]
                  w-[22px]
                  cursor-pointer
                  place-items-center
                  rounded-[6px]
                  border-0
                  bg-[rgba(255,255,255,0.92)]
                  p-0
                  text-st-ink
                  opacity-0
                  transition-opacity
                  hover:opacity-100
                  focus-visible:opacity-100
                  focus-visible:outline
                  focus-visible:outline-[2px]
                  focus-visible:outline-offset-1
                  focus-visible:outline-st-green
                  ${
                    isActive || isMenuOpen
                      ? "opacity-100"
                      : "group-hover:opacity-100"
                  }
                `}
                aria-label={`Page ${i + 1} options`}
                aria-expanded={isMenuOpen}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  const r = e.currentTarget.getBoundingClientRect();

                  setMenu(
                    isMenuOpen
                      ? null
                      : {
                          id,
                          left: r.left,
                          bottom:
                            window.innerHeight - r.top + 6,
                        },
                  );
                }}
              >
                <Icon name="more" size={16} />
              </button>

              {/* Page menu */}
              {isMenuOpen && (
                <div
                  className="
                    fixed
                    z-[60]
                    w-[180px]
                    overflow-hidden
                    rounded-10
                    border
                    border-st-line
                    bg-white
                    py-1
                    shadow-[0_12px_30px_-10px_rgba(0,0,0,0.35)]
                  "
                  role="menu"
                  style={{
                    left: menu.left,
                    bottom: menu.bottom,
                  }}
                  onMouseLeave={() => setMenu(null)}
                >
                  {/* Duplicate */}
                  <button
                    type="button"
                    role="menuitem"
                    className="
                      flex
                      w-full
                      cursor-pointer
                      items-center
                      border-0
                      bg-transparent
                      px-3
                      py-2.5
                      text-left
                      text-sm
                      text-st-ink
                      hover:bg-st-paper
                      focus-visible:bg-st-paper
                      focus-visible:outline-none
                    "
                    onClick={() => {
                      setMenu(null);
                      onDuplicate(i);
                    }}
                  >
                    <strong>Duplicate page</strong>
                  </button>

                  {/* Move left */}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={i === 0}
                    className="
                      flex
                      w-full
                      cursor-pointer
                      items-center
                      border-0
                      bg-transparent
                      px-3
                      py-2.5
                      text-left
                      text-sm
                      text-st-ink
                      hover:bg-st-paper
                      focus-visible:bg-st-paper
                      focus-visible:outline-none
                      disabled:cursor-not-allowed
                      disabled:opacity-40
                    "
                    onClick={() => {
                      setMenu(null);
                      onMove(i, -1);
                    }}
                  >
                    <strong>Move left</strong>
                  </button>

                  {/* Move right */}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={i === ids.length - 1}
                    className="
                      flex
                      w-full
                      cursor-pointer
                      items-center
                      border-0
                      bg-transparent
                      px-3
                      py-2.5
                      text-left
                      text-sm
                      text-st-ink
                      hover:bg-st-paper
                      focus-visible:bg-st-paper
                      focus-visible:outline-none
                      disabled:cursor-not-allowed
                      disabled:opacity-40
                    "
                    onClick={() => {
                      setMenu(null);
                      onMove(i, 1);
                    }}
                  >
                    <strong>Move right</strong>
                  </button>

                  {/* Delete */}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={ids.length === 1}
                    className="
                      flex
                      w-full
                      cursor-pointer
                      items-center
                      border-0
                      bg-transparent
                      px-3
                      py-2.5
                      text-left
                      text-sm
                      text-st-red
                      hover:bg-st-paper
                      focus-visible:bg-st-paper
                      focus-visible:outline-none
                      disabled:cursor-not-allowed
                      disabled:opacity-40
                    "
                    onClick={() => {
                      setMenu(null);
                      onDelete(i);
                    }}
                  >
                    <strong>Delete page</strong>
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Add page */}
        <button
          type="button"
          className="
            grid
            h-[54px]
            w-11
            flex-none
            cursor-pointer
            place-items-center
            rounded-[6px]
            border-[1.5px]
            border-dashed
            border-[#9fb3ab]
            bg-transparent
            p-0
            text-st-muted
            transition
            hover:border-st-green
            hover:bg-st-green-soft
            hover:text-st-green
            focus-visible:outline
            focus-visible:outline-[3px]
            focus-visible:outline-offset-2
            focus-visible:outline-[rgba(11,122,90,0.45)]
          "
          onClick={onAdd}
          title="Add page (Ctrl+Enter)"
          aria-label="Add page"
        >
          <Icon name="plus" size={20} />
        </button>
      </div>

      {/* Page count */}
      <span
        className="
          shrink-0
          whitespace-nowrap
          text-xs
          text-st-muted
        "
      >
        Page {index + 1} of {ids.length}
      </span>
    </div>
  );
}